import React, { FC, ReactElement, FormEvent, useState, useEffect, ChangeEvent } from 'react';
import cns from 'classnames';
import './RegisterPage.sass';
import ReactHtmlParser from 'react-html-parser';
import { useDispatch, useSelector } from 'react-redux';
import {
  getContentDataIntro,
  getEventKey,
  setUserEmail,
  setUserRegistrationFields,
  setIsRegisterInProgress,
  getEventParticipationType,
} from '../../../stores/reducers/userData';
import { RegistrationProps } from './types';
import { checkRegistrationByEmail, registerByEmail, userHost } from '../../../api';
import LoginModal, { PASSWORD_RESTORE_TIME } from '../../Modals/LoginModal/LoginModal';
import FormFields from './FormFields';
import localeFieldResolver from '../../../helpers/locale-field-finder';
import { internalizationState } from '../../../stores/reducers/sections';
import responseErrorChecker from 'src/helpers/response-error-checker';
import {storageFactory} from '../../../helpers/storage';
import routes from 'src/config/routes';
import { useHistory } from 'react-router-dom';

declare global {
  interface Window {
    createWidgetFrame: any;
  }
}


const local = storageFactory(() => localStorage);

const RegisterPage: FC<RegistrationProps> = ({
  className,
  bgSettings,
  // guestInfo,
  registrationFields,
  eventRules,
  sectionName,
  sectionId,
  allowedEmailDomain,
  onDomainError,
  sectionData
}): ReactElement => {
  const dispatch = useDispatch();
  const history = useHistory();
  const eventKey = useSelector(getEventKey);
  const contentData = useSelector(getContentDataIntro);
  const participationType = useSelector(getEventParticipationType);

  const [inputValues, setInputValues] = useState<any>({participationType: participationType !== 'hybrid' && participationType !== '' ? participationType : ''});
  const [isUserAgree, setIsUserAgree] = useState(false);
  const [loginModalData, setLoginModalData] = useState({ isOpen: false, title: '', notice: '', isFormHidden: false });
  const { activeLangState } = useSelector(internalizationState);

  const widgetRef = React.useRef(null);

  const localizeData = {
    registration_ru: 'Регистрация',
    registration_en: 'Registration',
    onDomainError_ru: 'Организатор ограничил доступ к мероприятию для данного доменного имени почты.',
    onDomainError_en: 'The organizer has restricted access to the event for this mail domain name.',
    registrationConfirm_ru: 'Поздравляем, вы успешно зарегистрированы на мероприятие!',
    registrationConfirm_en: 'Congratulations, you have successfully registered for the event!',
    registrationConfirmNotice_ru: `Мы направили вам на почту ${inputValues.email} письмо о подтверждении регистрации и одноразовый код для доступа на платформу. Вы можете воспользоваться им сейчас и перейти к мероприятию или сделать это позже в любой удобный момент, перейдя по ссылки из письма о регистрации. Если вы не можете найти наши письма, проверьте папку Спам или свяжитесь с поддержкой support@comdi.com.`,
    registrationConfirmNotice_en: `We have sent you a registration confirmation email and a one-time code to access the platform by email ${inputValues.email}. You can use it now and proceed to the event, or do it later at any time by following the link in the registration email. If you cannot find our emails, please check your Spam folder or contact support@comdi.com.`,
    login_ru: 'Войти',
    login_en: 'Login',
    loginNotice_ru: `Ваша почта ${inputValues.email} уже зарегистрирована на мероприятие "${localeFieldResolver(contentData, 'eventName', activeLangState)}". Мы отправили вам новый одноразовый код на почту. Введите его для перехода к мероприятию.`,
    loginNotice_en: `Your email ${inputValues.email} is already registered for the event "${localeFieldResolver(contentData, 'eventName', activeLangState)}". We have sent you a new one-time code by email. Enter it to go to the event.`,
    onlyNumbersAndLetters_ru: 'Только буквы и цифры',
    onlyNumbersAndLetters_en: 'Letters and numbers only',
    back_ru: 'Назад',
    back_en: 'Back',
    acceptConditionsShort_ru: 'Я принимаю все вышеперечисленные условия',
    acceptConditionsShort_en: 'I accept all of the above conditions',
    acceptConditions_ru: `
      Нажимая на кнопку, я принимаю условия
      <a href="https://comdi.com/legal/user-agreement.pdf" target="_blank">Пользовательского соглашения</a>
      и соглашаюсь на коммуникацию со мной в целях и способами, указанными в Пользовательском соглашении, а также
      <a href="https://www.comdi.com/legal/private-agreement-services.pdf" target="_blank">соглашаюсь</a>
      на обработку персональных данных в соответствии с
      <a href="https://www.comdi.com/legal/privacy-policy-services.pdf" target="_blank">Политикой</a>
      обработки персональных данных.
    `,
    acceptConditions_en: `
      By clicking the button, I accept the terms and conditions
      <a href="https://comdi.com/legal/user-agreement.pdf" target="_blank">User Agreement</a>
      and agree to communicate with me for the purposes and methods specified in the User Agreement, as well as
      <a href="https://www.comdi.com/legal/private-agreement-services.pdf" target="_blank">I agree</a>
      to process personal data in accordance with
      <a href="https://www.comdi.com/legal/privacy-policy-services.pdf" target="_blank">Policy</a>
      processing of personal data.
    `,
    registrationConfirmOffline_ru: `Подтверждение участия отправлено на электронный адрес ${inputValues.email}, указанный в форме регистрации. Если вы не можете найти письмо, проверьте папку Спам или свяжитесь с поддержкой support@comdi.com.`,
    registrationConfirmOffline_en: `Confirmation of participation has been sent to the email address ${inputValues.email} specified in the registration form. If you cannot find the email, check your Spam folder or contact support support@comdi.com.`,
    loginOffline_ru: `Ваша почта ${inputValues.email} уже зарегистрирована на мероприятие "${localeFieldResolver(contentData, 'eventName', activeLangState)}".`,
    loginOffline_en: `Your email ${inputValues.email} is already registered for the event "${localeFieldResolver(contentData, 'eventName', activeLangState)}".`,
  };

  const localizedName = (field: string): string => localeFieldResolver(localizeData, field, activeLangState);
  
  // useEffect(() => {
  //   if (guestInfo && guestInfo.email) dispatch(setUserEmail(guestInfo.email));
  //   window.dispatchEvent((window as any).regPageOpen);
  // }, [guestInfo, dispatch]);

  const checkRegistrationEmail = (email: string): boolean => {
    let domains: string[] = []
    const userDomain = email.split('@')[1];

    if (allowedEmailDomain && allowedEmailDomain.length) {
      domains = allowedEmailDomain.split(/[\s;,]+/);
      if (domains.includes(userDomain)) {
        return true;
      }
      return false;
    }

    return true;
  };

  const saveEventRegistrationFields = () => {
    if (registrationFields) {
      const fields: any = {};
      registrationFields.forEach((field) => {
        fields[field.field.name] = inputValues[field.field.name];
      });
      dispatch(setUserRegistrationFields(fields));
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    try {
      e.preventDefault();
      window.dispatchEvent((window as any).regFinished);
      saveEventRegistrationFields();
      // const name = guestInfo?.name || inputValues.name;
      // const surname = guestInfo?.surname || inputValues.surname;
      // const email = guestInfo?.email || inputValues.email;
      // const patronymic = guestInfo?.patronymic || inputValues.patronymic || '';
      if (inputValues.email) {
        if (!checkRegistrationEmail(inputValues.email)) {
          onDomainError(localizedName('onDomainError_ru'));
          return;
        }

        dispatch(setUserEmail(inputValues.email));

        const checkRegistrationByEmailResponse = await checkRegistrationByEmail(inputValues.email, eventKey) as Response;
        const checkRegistrationByEmailPermission = await responseErrorChecker(checkRegistrationByEmailResponse, activeLangState);
        if (!checkRegistrationByEmailPermission) return;

        const checkRegistrationByEmailData = await (checkRegistrationByEmailResponse).json();

        if (checkRegistrationByEmailData && Object.keys(checkRegistrationByEmailData).length) {

          const { status } = checkRegistrationByEmailData;
          dispatch(setIsRegisterInProgress(true));
          if (!status) {
            const registerByEmailResponse = await registerByEmail({...inputValues, termsAgree: isUserAgree}, eventKey) as Response;
            const registerByEmailPermission = await responseErrorChecker(registerByEmailResponse, activeLangState);

            if (!registerByEmailPermission) return;

            const registerByEmailData = await registerByEmailResponse.json();

            if (registerByEmailData && registerByEmailData.status) {
              local.setItem(PASSWORD_RESTORE_TIME, registerByEmailData?.passwordLockTime?.toString() ?? '');

              setLoginModalData({
                isOpen: true,
                isFormHidden: inputValues.participationType === 'offline',
                title: localizedName('registrationConfirm_ru'),
                notice: localizedName(inputValues.participationType !== 'offline' ? 'registrationConfirmNotice_ru' : 'registrationConfirmOffline_ru'),
              });
            }
          } else {
            if (checkRegistrationByEmailData.status && checkRegistrationByEmailData.passwordUpdateLock) {
              local.setItem(PASSWORD_RESTORE_TIME, checkRegistrationByEmailData.passwordUpdateLock.toString());
            }

            setLoginModalData({
              isOpen: true,
              isFormHidden: inputValues.participationType === 'offline',
              title: localizedName('login_ru'),
              notice: localizedName(inputValues.participationType !== 'offline' ? 'loginNotice_ru' : 'loginOffline_ru'),
            });
          }
        } else {
          console.error('checkRegistrationByEmail: bad response!');
        }

      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleFieldChange = (
    e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>,
    name: string | undefined
  ) => {
    const { target } = e;
    if (target.validity.patternMismatch) {
      target.setCustomValidity(localizedName('onlyNumbersAndLetters_ru'));
      window.scrollTo(0, 0);
    } else {
      target.setCustomValidity('');
    }

    if (name) {
      const value = target.type !== 'checkbox' ? target.value : (target as {checked: boolean}).checked;
      setInputValues({
        ...inputValues,
        [name]: value,
      });
    }
  };

  const getSettingParam = (type: string): string => {
    return sectionData.find((param) => param.type === type)?.value ?? ''
  }
  
  React.useEffect(() => {
    if (!!getSettingParam('widgetId') && typeof window.createWidgetFrame === 'function') {
      const host = process.env.NODE_ENV === 'development' ? 'https://dev-broadcast.comdi.com' : 'https://broadcast.comdi.com';
      const options = {
        widget: getSettingParam('widgetId'),
        backgroundColor: "FFFFFF",
        backgroundOpacityPercent: 100,
        colorAccent: "70bc69"
      };
      window.createWidgetFrame(
        widgetRef.current,
        `${host}/widget-registration/${eventKey}/registration`, 
        options
      );
    }
  }, [sectionData]);
  
  return (
    <>
      {loginModalData.isOpen && (
        <LoginModal
          isOpen={loginModalData.isOpen}
          title={loginModalData.title}
          notice={loginModalData.notice}
          isFormHidden={loginModalData.isFormHidden}
          goal="enterTheCode"
          isConfirmRegistrationMode
          closeModal={() => {
            setLoginModalData({ ...loginModalData, isOpen: false });
            history.push(routes.HOME)
          }}
        />
      )}
      <section
        className={cns(
          'static-page-section register',
          // bgSettings && bgSettings.isDark && 'inner-section--dark-bg',
          className
        )}
        style={{ backgroundColor: bgSettings?.color }}
        id={sectionId}
      >
        <div className="inner-section__head">
          <h1 style={{color: getSettingParam('titleColor')}}>
            {sectionName}
          </h1>
        </div>
        <div className="container">
          <div style={{ padding: '14px 0' }} />
          {
            !!getSettingParam('widgetId')
            ? <div ref={widgetRef} id="widget-registration"></div>
            : <form className="registration-form" onSubmit={(e) => handleSubmit(e)}>
                <FormFields
                  registrationFields={registrationFields}
                  inputValues={inputValues}
                  participationType={participationType}
                  handleFieldChange={handleFieldChange}
                />
                <div className="wrapper-agreement">
                  <div className="agreement-text">
                    {
                      eventRules && eventRules.rules === true ?
                        ReactHtmlParser(localizedName('acceptConditions_ru'))
                      :
                        <></>
                    }
                    <br />
                    <br />
                    { eventRules && ReactHtmlParser(localeFieldResolver(eventRules, 'customRulesRu', activeLangState)) }
                  </div>
                  { eventRules && (eventRules.rules === true || eventRules?.customRulesRu || eventRules?.customRulesEn) ?
                      <label>
                        <span className="agreement-text">{ localizedName('acceptConditionsShort_ru') }</span>
                        <input type="checkbox" checked={isUserAgree} onChange={() => setIsUserAgree(!isUserAgree)} />
                        <span className="checkmark" />
                      </label>
                    :
                      <></>
                  }
                </div>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={!isUserAgree}
                  style={{
                    backgroundColor: isUserAgree ? getSettingParam('buttonColor') : '',
                    color: isUserAgree ? getSettingParam('buttonFontColor') : ''
                  }}
                >
                  { localizedName('registration_ru') }
                </button>
              </form>
          }
        </div>
      </section>
    </>
  );
};

export default RegisterPage;
